import { NestedMenuItem } from '@/lib/queries/Menus';
import { getMenuItemLabel } from '@/lib/queries/Menus';
import { getPageUrl, normalizeLinkHref } from '@/lib/UrlHelpers';

import {
  BASE_FONT_SIZE,
  BASE_INDENTATION,
  FONT_SIZE_DECREMENT,
  INDENTATION_MULTIPLIER,
  MIN_FONT_SIZE,
} from './MenuConstants';

/**
 * Helper function to get the URL for a menu item (no trailing slash).
 */
export const getMenuItemUrl = (item: NestedMenuItem): string => {
  let url: string;
  if (item.MenuUrl) {
    url = item.MenuUrl;
  } else {
    switch (item.__typename) {
      case 'ComponentMenuMenuLinkPage': {
        const path = item.Page ? getPageUrl(item.Page) : '#';
        url = path === '#' ? '#' : `${path}${item.PageAnchor ? `#${item.PageAnchor}` : ''}`;
        break;
      }
      case 'ComponentMenuMenuLinkUrl':
        url = item.Url || '#';
        break;
      case 'ComponentMenuMenuLinkResource':
        url = `/resources/${item.resource_article?.ResourceCategory?.Slug}/${item.resource_article?.Slug}`;
        break;
      case 'ComponentMenuMenuLinkResourceCategory':
        url = `/resources/${item.resource_category?.Slug}`;
        break;
      default:
        url = '#';
    }
  }
  return normalizeLinkHref(url);
};

/**
 * Helper function to get the display text for a menu item
 */
export const getDisplayText = (item: NestedMenuItem): string => {
  const customLabel = getMenuItemLabel(item);
  if (customLabel) return customLabel;

  if (item.MenuTitle) {
    return item.MenuTitle;
  }

  switch (item.__typename) {
    case 'ComponentMenuMenuLinkPage':
      return item.Page?.Title || 'Untitled Page';
    case 'ComponentMenuMenuLinkResource':
      return item.resource_article?.ResourceTitle || 'Untitled Resource';
    case 'ComponentMenuMenuLinkResourceCategory':
      return item.resource_category?.CategoryName || 'Untitled Category';
    case 'ComponentMenuMenuSubMenu':
      return item.SubMenu?.MenuName || 'Menu';
    default:
      return 'Menu Item';
  }
};

/**
 * Helper function to get the path for a menu item (for active state detection)
 */
const getItemPath = (menuItem: NestedMenuItem): string => {
  if (menuItem.MenuUrl) {
    return menuItem.MenuUrl;
  }

  switch (menuItem.__typename) {
    case 'ComponentMenuMenuLinkPage': {
      const path = menuItem.Page ? getPageUrl(menuItem.Page) : '';
      return path ? `${path}${menuItem.PageAnchor ? `#${menuItem.PageAnchor}` : ''}` : '';
    }
    case 'ComponentMenuMenuLinkUrl':
      return menuItem.Url || '';
    case 'ComponentMenuMenuLinkResource':
      return `/resources/${menuItem.resource_article?.ResourceCategory?.Slug}/${menuItem.resource_article?.Slug}`;
    case 'ComponentMenuMenuLinkResourceCategory':
      return `/resources/${menuItem.resource_category?.Slug}`;
    default:
      return '';
  }
};

/**
 * Helper function to check if a menu item is active (matches current path).
 * Both paths are normalized (no trailing slash) so comparison is consistent.
 */
export const isMenuItemActive = (item: NestedMenuItem, currentPath: string): boolean => {
  const itemPath = getItemPath(item);
  return normalizeLinkHref(itemPath) === normalizeLinkHref(currentPath);
};

/**
 * Helper function to check if a menu item is an active parent (has active children)
 */
export const isMenuItemActiveParent = (item: NestedMenuItem, currentPath: string): boolean => {
  if (item.MenuItems && item.MenuItems.length > 0) {
    return checkForActiveChild(item.MenuItems, currentPath);
  }

  if (item.__typename !== 'ComponentMenuMenuSubMenu' || !item.SubMenu?.MenuItems) {
    return false;
  }

  return checkForActiveChild(item.SubMenu.MenuItems, currentPath);
};

// Helper function to recursively check for active children
const checkForActiveChild = (menuItems: NestedMenuItem[], currentPath: string): boolean => {
  return menuItems.some((subItem) => {
    // Check if this item is active
    if (isMenuItemActive(subItem, currentPath)) {
      return true;
    }
    // Recursively check if this item has active children
    if (subItem.MenuItems && subItem.MenuItems.length > 0) {
      return checkForActiveChild(subItem.MenuItems, currentPath);
    }
    if (subItem.__typename === 'ComponentMenuMenuSubMenu' && subItem.SubMenu?.MenuItems) {
      return checkForActiveChild(subItem.SubMenu.MenuItems, currentPath);
    }
    return false;
  });
};

/**
 * Helper function to check if a menu item has a submenu
 */
export const hasSubmenu = (item: NestedMenuItem): boolean => {
  if (item.MenuItems && item.MenuItems.length > 0) {
    return true;
  }

  return item.__typename === 'ComponentMenuMenuSubMenu' && !!item.SubMenu?.MenuItems;
};

/**
 * Helper function to generate a unique key for a menu item
 */
export const generateMenuItemKey = (
  item: NestedMenuItem,
  index: number,
  prefix: string = '',
  depth: number = 0
): string => {
  const parts = [prefix, item.__typename, depth.toString(), index.toString()];
  return parts.filter(Boolean).join('-');
};

/**
 * Helper function to calculate indentation padding based on depth
 */
export const getIndentationPadding = (depth: number): number => {
  return depth * INDENTATION_MULTIPLIER + BASE_INDENTATION;
};

/**
 * Helper function to calculate font size based on depth
 */
export const getFontSize = (depth: number): number => {
  return Math.max(MIN_FONT_SIZE, BASE_FONT_SIZE - depth * FONT_SIZE_DECREMENT);
};

/**
 * Helper function to close all menus and clear timeouts
 */
export const closeAllMenus = (
  setExpandedItems: React.Dispatch<React.SetStateAction<{ [key: string]: boolean }>>,
  setMobileOpen?: React.Dispatch<React.SetStateAction<boolean>>
) => {
  setExpandedItems({});
  if (setMobileOpen) setMobileOpen(false);
};
